import { generateClient } from 'aws-amplify/api';
import {
  Expense,
  CreateExpenseInput,
  UpdateExpenseInput,
  ExpenseStatus,
  ExpenseConnection,
  PaginationParams,
  ExpenseFilters,
  GraphQLResponse,
  User
} from '../types';
import { 
  CREATE_EXPENSE, 
  UPDATE_EXPENSE, 
  SUBMIT_EXPENSE, 
  DELETE_EXPENSE 
} from './graphql/mutations';
import { GET_MY_EXPENSES, GET_TEAM_EXPENSES, GET_ALL_EXPENSES } from './graphql/queries';
import { NotificationService } from './notification';
import { retryGraphQLOperation } from '../utils/retry';
import { errorHandler } from './errorHandler';

// Lazy initialize client to avoid "Amplify not configured" error
// `any`, as in leave.ts: the generated type sends TypeScript 5+ (the mobile app) into "excessive
// stack depth" (TS2321), and nothing here relies on it.
let client: any = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

/**
 * Service for handling expense operations with comprehensive error handling
 * Requirements: 2.3, 2.4 - Draft and submission functionality
 * Requirements: 5.5, 7.5, 10.2 - Error handling with retry logic
 */
export class ExpenseService {
  
  /**
   * Creates a new expense in draft status
   * Requirements: 2.3 - Save as draft capability
   */
  static async createDraft(input: CreateExpenseInput): Promise<Expense> {
    try {
      const response = await retryGraphQLOperation(async () => {
        return await getClient().graphql({
          query: CREATE_EXPENSE,
          variables: { input }
        }) as GraphQLResponse<{ createExpense: Expense }>;
      });

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.createExpense) {
        throw new Error('Failed to create expense');
      }

      return response.data.createExpense;
    } catch (error) {
      const appError = errorHandler.handleError(error);
      console.error('Failed to create draft expense:', appError);
      throw new Error(
        error instanceof Error 
          ? `Failed to create draft: ${error.message}` 
          : 'Failed to create draft expense'
      );
    }
  }

  /**
   * Updates an existing draft expense
   * Requirements: 2.3 - Draft editing capability
   */
  static async updateDraft(id: string, input: UpdateExpenseInput): Promise<Expense> {
    try {
      const response = await getClient().graphql({
        query: UPDATE_EXPENSE,
        variables: { id, input }
      }) as GraphQLResponse<{ updateExpense: Expense }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.updateExpense) {
        throw new Error('Failed to update expense');
      }

      return response.data.updateExpense;
    } catch (error) {
      console.error('Failed to update draft expense:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to update draft: ${error.message}` 
          : 'Failed to update draft expense'
      );
    }
  }

  /**
   * Submits an expense for approval with notification triggers
   * Requirements: 2.4, 5.1 - Submit for approval with status change and notifications
   */
  static async submitForApproval(id: string, manager?: User): Promise<Expense> {
    try {
      const response = await getClient().graphql({
        query: SUBMIT_EXPENSE,
        variables: { id }
      }) as GraphQLResponse<{ submitExpense: Expense }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.submitExpense) {
        throw new Error('Failed to submit expense');
      }

      const submittedExpense = response.data.submitExpense;

      // Trigger notification to manager
      try {
        if (manager) {
          await NotificationService.notifyExpenseSubmitted(submittedExpense, manager);
        }
      } catch (notificationError) {
        console.error('Failed to send submission notification:', notificationError);
      }

      return submittedExpense;
    } catch (error) {
      console.error('Failed to submit expense:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to submit expense: ${error.message}` 
          : 'Failed to submit expense for approval'
      );
    }
  }

  /**
   * Deletes a draft expense
   * Requirements: 2.3 - Draft deletion capability
   */
  static async deleteDraft(id: string): Promise<void> {
    try {
      const response = await getClient().graphql({
        query: DELETE_EXPENSE,
        variables: { id }
      }) as GraphQLResponse<{ deleteExpense: { success: boolean; message: string } }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.deleteExpense?.success) {
        throw new Error(response.data?.deleteExpense?.message || 'Failed to delete expense');
      }
    } catch (error) {
      console.error('Failed to delete draft expense:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to delete draft: ${error.message}` 
          : 'Failed to delete draft expense'
      );
    }
  }

  /**
   * Gets user's expenses with pagination support
   * Requirements: 10.4 - Efficient pagination for large expense lists
   */
  static async getMyExpenses(
    filters?: ExpenseFilters,
    pagination?: PaginationParams
  ): Promise<ExpenseConnection> {
    try {
      const variables = {
        status: filters?.status,
        limit: pagination?.limit || 20,
        nextToken: pagination?.nextToken,
        sortDirection: pagination?.sortDirection || 'DESC'
      };

      const response = await getClient().graphql({
        query: GET_MY_EXPENSES,
        variables
      }) as GraphQLResponse<{ getMyExpenses: ExpenseConnection }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      return response.data?.getMyExpenses || { items: [], nextToken: undefined, total: 0 };
    } catch (error) {
      console.error('Failed to get expenses:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to get expenses: ${error.message}` 
          : 'Failed to retrieve expenses'
      );
    }
  }

  /**
   * Gets team expenses for managers with pagination support
   * Requirements: 6.2, 10.4 - Manager team expense overview with pagination
   */
  static async getTeamExpenses(
    filters?: ExpenseFilters,
    pagination?: PaginationParams
  ): Promise<ExpenseConnection> {
    try {
      const variables = {
        status: filters?.status,
        limit: pagination?.limit || 20,
        nextToken: pagination?.nextToken,
        sortDirection: pagination?.sortDirection || 'DESC'
      };

      const response = await getClient().graphql({
        query: GET_TEAM_EXPENSES,
        variables
      }) as GraphQLResponse<{ getTeamExpenses: ExpenseConnection }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      return response.data?.getTeamExpenses || { items: [], nextToken: undefined, total: 0 };
    } catch (error) {
      console.error('Failed to get team expenses:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to get team expenses: ${error.message}` 
          : 'Failed to retrieve team expenses'
      );
    }
  }

  /**
   * Gets all expenses (admin access) with pagination support
   * Requirements: 4.1, 10.4 - Finance admin universal access with pagination
   */
  static async getAllExpenses(
    filters?: ExpenseFilters & { dateFrom?: string; dateTo?: string },
    pagination?: PaginationParams
  ): Promise<ExpenseConnection> {
    try {
      const variables = {
        status: filters?.status,
        department: filters?.department,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
        limit: pagination?.limit || 20,
        nextToken: pagination?.nextToken,
        sortDirection: pagination?.sortDirection || 'DESC'
      };

      const response = await getClient().graphql({
        query: GET_ALL_EXPENSES,
        variables
      }) as GraphQLResponse<{ getAllExpenses: ExpenseConnection }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      return response.data?.getAllExpenses || { items: [], nextToken: undefined, total: 0 };
    } catch (error) {
      console.error('Failed to get all expenses:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to get all expenses: ${error.message}` 
          : 'Failed to retrieve all expenses'
      );
    }
  }

  /**
   * Validates expense data before submission
   * Requirements: 2.4 - Validation before submission
   */
  static validateForSubmission(expense: Expense): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!expense.title || expense.title.trim().length === 0) {
      errors.push('Title is required');
    }

    if (!expense.category) {
      errors.push('Category is required');
    }

    if (!expense.amount || expense.amount <= 0) {
      errors.push('Amount must be greater than 0');
    }

    if (!expense.date) {
      errors.push('Date is required');
    }

    if (!expense.paymentMethod) {
      errors.push('Payment method is required');
    }

    if (expense.date) {
      const expenseDate = new Date(expense.date);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (expenseDate > today) {
        errors.push('Expense date cannot be in the future');
      }
    }

    if (expense.amount) {
      const decimalPlaces = (expense.amount.toString().split('.')[1] || '').length;
      if (decimalPlaces > 2) {
        errors.push('Amount cannot have more than 2 decimal places');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static canEdit(expense: Expense): boolean {
    return expense.status === ExpenseStatus.DRAFT;
  }

  static canDelete(expense: Expense): boolean {
    return expense.status === ExpenseStatus.DRAFT || expense.status === ExpenseStatus.SUBMITTED;
  }

  static canSubmit(expense: Expense): boolean {
    if (expense.status !== ExpenseStatus.DRAFT) {
      return false;
    }
    const validation = this.validateForSubmission(expense);
    return validation.isValid;
  }

  static getNextStatusAfterSubmission(): ExpenseStatus {
    return ExpenseStatus.SUBMITTED;
  }

  static formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  static getStatusColor(status: ExpenseStatus): string {
    switch (status) {
      case ExpenseStatus.DRAFT:
        return 'gray';
      case ExpenseStatus.SUBMITTED:
        return 'blue';
      case ExpenseStatus.MANAGER_APPROVED:
        return 'green';
      case ExpenseStatus.MANAGER_REJECTED:
        return 'red';
      case ExpenseStatus.FINANCE_APPROVED:
        return 'green';
      case ExpenseStatus.FINANCE_REJECTED:
        return 'red';
      case ExpenseStatus.REIMBURSED:
        return 'purple';
      default:
        return 'gray';
    }
  }

  static getStatusDescription(status: ExpenseStatus): string {
    switch (status) {
      case ExpenseStatus.DRAFT:
        return 'Draft - Not yet submitted';
      case ExpenseStatus.SUBMITTED:
        return 'Submitted - Awaiting manager approval';
      case ExpenseStatus.MANAGER_APPROVED:
        return 'Manager Approved - Awaiting finance approval';
      case ExpenseStatus.MANAGER_REJECTED:
        return 'Manager Rejected - Requires revision';
      case ExpenseStatus.FINANCE_APPROVED:
        return 'Finance Approved - Awaiting reimbursement';
      case ExpenseStatus.FINANCE_REJECTED:
        return 'Finance Rejected - Not approved for reimbursement';
      case ExpenseStatus.REIMBURSED:
        return 'Reimbursed - Payment completed';
      default:
        return status;
    }
  }
}
